import { Plane, Vector3 } from "three";
/**
 * TarversalsState Class
 * @class TraversalState
 * @param {Scene} scene the current scene
 * @constructor
 */
export class TraversalState {
    /**
     * @method constructor
     * @param {Scene} scene the current scene
     */
    constructor(scene) {
        /**
         * hold any available cameras
         * @property {Object} cameras
         * @default {}
         */
        this.cameras = {};
        /**
         * @property {boolean} mouseClick
         * @default false
         */
        this.mouseClick = false;
        /**
         * @property {boolean} mouseUp
         * @default false
         */
        this.mouseUp = false;
        /**
         * @property {PointerEvent} mouseUpArguments
         * @default null
         */
        this.mouseUpArguments = null;
        /**
         * @property {boolean} mouseDown
         * @default false
         */
        this.mouseDown = false;
        /**
         * @property {PointerEvent} mouseDownArguments
         * @default null
         */
        this.mouseDownArguments = null;
        /**
         * @property {boolean} mouseMove
         * @default false
         */
        this.mouseMove = false;
        /**
         * @property {MouseEvent} mouseMoveArguments
         * @default null
         */
        this.mouseMoveArguments = null;
        /**
         * @property {boolean} mouseWheel
         * @default false
         */
        this.mouseWheel = false;
        /**
         * @property {Object} mouseWheelArguments
         * @default null
         */
        this.mouseWheelArguments = null;
        /**
         * @property {boolean} pan
         * @default false
         */
        this.pan = false;
        /**
         * @property {Object} mousePosX
         * @default null
         */
        this.mousePosX = null;
        /**
         * @property {Object} mousePosY
         * @default null
         */
        this.mousePosY = null;
        /**
         * @property {boolean} keyPress
         * @default false
         */
        this.keyPress = false;
        /**
         * @property {boolean} keyDown
         * @default false
         */
        this.keyDown = false;
        /**
         * @property {boolean} keyUp
         * @default false
         */
        this.keyUp = false;
        /**
         * @property {number} key
         * @default -1
         */
        this.key = -1;
        /**
         * @property {number} gamma
         * @default 1.0
         */
        this.gamma = 1.0;
        /**
         * @property {boolean} render
         * @default true
         */
        this.render = true;
        /**
         * @property {Camera} actualCamera
         * @default null
         */
        this.actualCamera = null;
        /**
         * @property {Camera} actualSceneCamera for rendering geometries
         * @default null
         */
        this.actualSceneCamera = null;
        /**
         * @property {Camera} actualCamera
         * @default null
         */
        this.actualQuadtree = null;
        /**
         * @property {boolean} lockRendering
         * @default false
         */
        this.lockRendering = false;
        /**
         * @property {boolean} lockNavigation
         * @default false
         */
        this.lockNavigation = false;
        /**
         * @property {number} _animid
         * @private
         * @default -1
         */
        this._animid = -1;
        this.t = -1;
        this.panX = 0;
        this.panY = 0;
        this.zoom = 0;
        this.panArguments = { offsetX: 0, offsetY: 0 };
        this.sceneOffsetX = 0;
        this.sceneOffsetY = 0;
        /**
         * is true, when canvas was resized...
         * @type {boolean}
         */
        this.resize = false;
        this.resizeArguments = { newwidth: 0, newheight: 0 };
        this.rayCaster = null;
        this.rayCasterQuadtreeScene = null;
        /**
         * @property {Object} matModel Model Matrix
         * @default null
         */
        this.matModel = null;
        /**
         * @property {Object} matProjection Projection Matrix
         * @default null
         */
        this.matProjection = null;
        /**
         * @property {Object} matMVP Model-View-Projection-Matrix
         * @default null
         */
        this.matMVP = null;
        this.matZoom = null;
        /**
         * @property {Object} matModelView ModelView Matrix
         * @default null
         */
        this.matModelView = null;
        /**
         * indicates wheter the ground plane was updated i.e. updated the scene camera position
         * @type {boolean}
         */
        this.groundPlaneUpdated = false;
        /**
         * use for drawing 2d features...
         * @type {Plane}
         */
        this.groundPlane = new Plane();
        this.cameraIntrinsics = null;
        this.perspectiveCamera = null;
        this.actualMousePos = [-1, -1];
        this.drag = false;
        /**
         * @property {boolean} sceneCamPosChanged indicate when position of scenecam was changed...
         * @default false
         */
        this.sceneCamPosChanged = false;
        /**
         * @property {Vector3} sceneCamPos current sceneCam Position
         * @default
         */
        this.sceneCamPos = new Vector3();
        /**
         * @property {boolean} sceneCamPosChanged indicate when position of scenecam was changed...
         * @default false
         */
        this.panoCamChanged = false;
        /**
         * @property {Vector3} panoCamPos current panoCam Position
         * @default
         */
        this.panoCamPos = new Vector3();
        /**
         * Indicates zoom 0=no zoom -> 1= fully zommed in.
         * @type {number}
         */
        this.panoZoom = 0;
        /**
         * Indicates zoom 0=no zoom -> 1= fully zommed in.
         * @type {number}
         */
        this.sceneZoom = 0;
        /**
         * Is true if panorama-view is active
         * @type {boolean}
         */
        this.panoMode = false;
        /**
         * Is true if multiPerspectiveMode-view is active
         * @type {boolean}
         */
        this.multiPerspectiveMode = false;
        /**
         * Opacity of cameraplane in navigation mode not highlighted
         * @property {number} _camplaneopacity
         */
        this.camplaneopacity = 0.5;
        /**
         * Opacity of cameraplane in pano-navigation mode
         * @property {number} _panocamplaneopacity
         */
        this.panocamplaneopacity = 0.15;
        this.scene = scene;
    }
    resetUserEvents() {
        this.mouseUp = false;
        this.mouseUpArguments = null;
        this.mouseDown = false;
        this.mouseDownArguments = null;
        this.mouseClick = false;
        this.mouseWheel = false;
        this.mouseWheelArguments = null;
        this.keyPress = false;
        this.keyDown = false;
        this.keyUp = false;
        this.pan = false;
        this.drag = false;
        this.sceneCamPosChanged = false;
        this.groundPlaneUpdated = false;
        this.resize = false;
        this.sceneZoom = -1;
        this.panoZoom = -1;
    }
}
//# sourceMappingURL=traversalstate.js.map